import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ─── Types Sites & Salles ───────────────────────────────────────────────────

export interface SiteExamen {
  id: number;
  nom: string;
  adresse: string;
  description: string;
  ordre_affichage: number;
  actif: boolean;
  nombre_salles: number;
  capacite_totale: number;
  date_creation: string;
  date_modification: string;
}

export interface SiteExamenCreate {
  nom: string;
  adresse?: string;
  description?: string;
  ordre_affichage?: number;
  actif?: boolean;
}

export interface SalleExamen {
  id: number;
  site: number;
  site_nom: string;
  nom: string;
  capacite: number;
  description: string;
  actif: boolean;
  date_creation: string;
  date_modification: string;
}

export interface SalleExamenCreate {
  site: number;
  nom: string;
  capacite: number;
  description?: string;
  actif?: boolean;
}

// ─── Types Brassage ─────────────────────────────────────────────────────────

export interface SessionCouverture {
  total_en_base: number;
  total_places: number;
  total_manquants: number;
  taux_couverture: number;
  alerte: BrassageAlerte | null;
  classes_oubliees: ClasseOubliee[];
  filtres_label: string;
}

export interface SessionBrassage {
  id: number;
  annee_academique: string;
  mode: 'par_pole' | 'par_niveau' | 'par_classe';
  mode_display: string;
  description: string;
  filtres_json: Record<string, unknown>;
  nombre_etudiants: number;
  nombre_salles_utilisees: number;
  nombre_sites_utilises: number;
  etudiants_non_places: number;
  genere_par: number | null;
  genere_par_nom: string | null;
  date_generation: string;
  visible_aux_etudiants?: boolean;
  date_visibilite?: string | null;
  visibilite_par_nom?: string;
  couverture?: SessionCouverture;
}

export interface PlacementEtudiant {
  id: number;
  etudiant: number;
  etudiant_nom: string;
  etudiant_prenoms: string;
  etudiant_sexe: string;
  etudiant_date_naissance: string | null;
  etudiant_lieu_naissance: string;
  etudiant_classe: string;
  etudiant_pole: string;
  etudiant_niveau: string;
  salle: number;              // FK id (utile pour identifier la salle actuelle lors des déplacements)
  site_nom: string;
  salle_nom: string;
}

export interface GenererBrassageParams {
  annee_academique: string;
  mode: 'par_pole' | 'par_niveau' | 'par_classe';
  description?: string;
  niveaux?: string[];
  poles?: string[];
  sites?: number[];
  classes?: string[];
  taux_remplissage?: number;
  /** Mode rattrapage : ne brasser que les étudiants ayant ≥1 ECUE non validée (matières non-TP) */
  seulement_a_rattraper?: boolean;
  /** 2026-09-15 : semestre du rattrapage (2 = règle des semestres pairs, 1 = règle historique) */
  semestre?: 1 | 2;
  /** Étendre le périmètre aux étudiants CS (Formation Continue / Cours du Soir) en plus des FI/DD. */
  inclure_cs?: boolean;
  /** Cibler exclusivement les étudiants CS (Formation Continue). Prioritaire sur inclure_cs. */
  cs_uniquement?: boolean;
}

export interface RapportRattrapagePair {
  regle: string;
  annee_academique: string;
  mois_requis: number;
  total_examines: number;
  nb_inclus: number;
  nb_exclus: number;
  nb_resultats_calcules: number;
  categories: { code: string; libelle: string; inclus: boolean; nb: number }[];
  par_niveau: { niveau: string; inclus: number; exclus: number }[];
  alertes: string[];
}

export interface CandidatsRattrapage {
  total: number;
  par_niveau: { niveau: string; nb: number }[];
  par_pole: { pole: string; nb: number }[];
  par_classe: { classe: string; nb: number }[];
  rattrapage?: RapportRattrapagePair | null;
}

// ─── Types Stats & Alertes ──────────────────────────────────────────────────

export interface BrassageAlerte {
  type: 'success' | 'warning' | 'danger';
  message: string;
}

export interface ClasseOubliee {
  classe: string;
  effectif: number;
}

export interface StatParClasse {
  classe: string;
  en_base: number;
  genere: number;
  manquant: number;
}

export interface BrassageStats {
  total_en_base: number;
  total_genere: number;
  total_places: number;
  total_non_places: number;
  taux_couverture: number;
  capacite_reelle: number;
  capacite_effective: number;
  taux_remplissage: number;
  nombre_salles: number;
  nombre_sites: number;
  alertes: BrassageAlerte[];
  classes_oubliees: ClasseOubliee[];
  stats_par_classe: StatParClasse[];
}

export interface EtudiantManquant {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  pole: string;
  niveau: string;
  type_formation: string;
}

export interface VerificationReport {
  total_etudiants_actifs: number;
  total_places: number;
  total_manquants: number;
  taux_couverture: number;
  alertes: BrassageAlerte[];
  etudiants_manquants: EtudiantManquant[];
  repartition_manquants: { pole: string; niveau: string; count: number }[];
  classes_manquantes: { classe: string; count: number }[];
  sessions_analysees: {
    session_id: number;
    description: string;
    nombre_etudiants: number;
    filtres: Record<string, unknown>;
    date_generation: string | null;
  }[];
}

export interface StatsSessionResponse {
  session_id: number;
  description: string;
  filtres: Record<string, unknown>;
  total_en_base: number;
  total_places: number;
  total_manquants: number;
  taux_couverture: number;
  alertes: BrassageAlerte[];
  classes_oubliees: ClasseOubliee[];
  etudiants_manquants: EtudiantManquant[];
}

export interface VerificationParams {
  session_ids: number[];
  poles?: string[];
  niveaux?: string[];
  classes?: string[];
}

export interface CompactageInfo {
  actif: boolean;
  nb_salles_utilisees: number;
  nb_salles_ignorees: number;
  salles_ignorees_noms: string[];
  capacite_utilisee: number;
  capacite_disponible_initiale: number;
  occupation_cible_pct?: number | null;
  taux_occupation_estime: number;
}

export interface GenererBrassageResponse {
  session_id: number;
  nombre_etudiants: number;
  nombre_salles_utilisees: number;
  nombre_sites_utilises: number;
  etudiants_non_places: number;
  capacite_totale: number;
  capacite_reelle: number;
  taux_remplissage: number;
  stats: BrassageStats;
  compactage?: CompactageInfo | null;
  rattrapage?: RapportRattrapagePair | null;
  message: string;
}

export interface GenererBrassageSessionParams {
  session_examen_id: number;
  description?: string;
}

export interface ResultatGroupe {
  niveau: string;
  pole: string;
  etudiants: number;
  places: number;
  non_places: number;
  vague?: string;
  sites?: string[];
  salles?: number;
  capacite?: number;
  erreur?: string;
}

export interface GenererBrassageSessionResponse {
  session_brassage_id: number;
  session_examen_id: number;
  nombre_etudiants: number;
  nombre_salles_utilisees: number;
  nombre_sites_utilises: number;
  etudiants_non_places: number;
  resultats_par_groupe: ResultatGroupe[];
  avertissements?: string[];
  rattrapage?: RapportRattrapagePair | null;
  message: string;
}

export interface ResultatsParSalle {
  site_nom: string;
  salle_nom: string;
  salle_capacite: number;
  effectif: number;
  placements: PlacementEtudiant[];
}

export interface ResultatsParSite {
  site_nom: string;
  salles: ResultatsParSalle[];
  effectif_total: number;
}

export interface ResultatsParClasse {
  classe: string;
  effectif: number;
  placements: PlacementEtudiant[];
}

export interface HistoriqueBrassageResponse {
  total: number;
  ce_mois: number;
  sessions: SessionBrassage[];
}

// ─── API Slice ──────────────────────────────────────────────────────────────

export const brassageApi = createApi({
  reducerPath: 'brassageApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['SitesExamens', 'SallesExamens', 'Brassage'],
  endpoints: (builder) => ({
    // ─── SITES CRUD ───
    getSites: builder.query<SiteExamen[], { actif?: boolean } | void>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params && params.actif !== undefined) qs.set('actif', String(params.actif));
        const s = qs.toString();
        return `/sites-examens/${s ? '?' + s : ''}`;
      },
      transformResponse: (response: any) => {
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['SitesExamens'],
    }),
    createSite: builder.mutation<SiteExamen, SiteExamenCreate>({
      query: (body) => ({ url: '/sites-examens/', method: 'POST', body }),
      invalidatesTags: ['SitesExamens'],
    }),
    updateSite: builder.mutation<SiteExamen, { id: number; data: Partial<SiteExamenCreate> }>({
      query: ({ id, data }) => ({ url: `/sites-examens/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: ['SitesExamens'],
    }),
    deleteSite: builder.mutation<void, number>({
      query: (id) => ({ url: `/sites-examens/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['SitesExamens', 'SallesExamens'],
    }),

    // ─── SALLES CRUD ───
    getSalles: builder.query<SalleExamen[], { site?: number; actif?: boolean } | void>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params && params.site) qs.set('site', String(params.site));
        if (params && params.actif !== undefined) qs.set('actif', String(params.actif));
        const s = qs.toString();
        return `/salles-examens/${s ? '?' + s : ''}`;
      },
      transformResponse: (response: any) => {
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['SallesExamens'],
    }),
    createSalle: builder.mutation<SalleExamen, SalleExamenCreate>({
      query: (body) => ({ url: '/salles-examens/', method: 'POST', body }),
      invalidatesTags: ['SallesExamens', 'SitesExamens'],
    }),
    updateSalle: builder.mutation<SalleExamen, { id: number; data: Partial<SalleExamenCreate> }>({
      query: ({ id, data }) => ({ url: `/salles-examens/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: ['SallesExamens', 'SitesExamens'],
    }),
    deleteSalle: builder.mutation<void, number>({
      query: (id) => ({ url: `/salles-examens/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['SallesExamens', 'SitesExamens'],
    }),

    // ─── BRASSAGE GÉNÉRATION ───
    genererBrassage: builder.mutation<GenererBrassageResponse, GenererBrassageParams>({
      query: (body) => ({ url: '/brassage/generer/', method: 'POST', body }),
      invalidatesTags: ['Brassage'],
    }),
    genererBrassagePourSession: builder.mutation<GenererBrassageSessionResponse, GenererBrassageSessionParams>({
      query: (body) => ({ url: '/brassage/generer-pour-session/', method: 'POST', body }),
      invalidatesTags: ['Brassage'],
    }),

    // ─── RÉSULTATS ───
    getResultatsParSalle: builder.query<ResultatsParSalle[], number>({
      query: (sessionId) => `/brassage/${sessionId}/resultats-par-salle/`,
    }),
    getResultatsParSite: builder.query<ResultatsParSite[], number>({
      query: (sessionId) => `/brassage/${sessionId}/resultats-par-site/`,
    }),
    getResultatsParClasse: builder.query<ResultatsParClasse[], { sessionId: number; classe?: string }>({
      query: ({ sessionId, classe }) => {
        const params = classe ? `?classe=${encodeURIComponent(classe)}` : '';
        return `/brassage/${sessionId}/resultats-par-classe/${params}`;
      },
    }),

    // ─── VÉRIFICATION ───
    verifierBrassage: builder.mutation<VerificationReport, VerificationParams>({
      query: (body) => ({ url: '/brassage/verification/', method: 'POST', body }),
    }),
    getStatsSession: builder.query<StatsSessionResponse, number>({
      query: (sessionId) => `/brassage/${sessionId}/stats/`,
    }),

    // ─── SUPPRESSION ───
    deleteSessionBrassage: builder.mutation<{ success: boolean; message: string }, number>({
      query: (id) => ({
        url: `/brassage/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Brassage'],
    }),

    // ─── ÉDITION DESCRIPTION ───
    modifierDescriptionBrassage: builder.mutation<
      { ok: boolean; session_id: number; description: string; longueur: number },
      { id: number; description: string }
    >({
      query: ({ id, description }) => ({
        url: `/brassage/${id}/modifier-description/`,
        method: 'POST',
        body: { description },
      }),
      invalidatesTags: ['Brassage'],
    }),

    // ─── RÉPARTITION SITES (info détaillée) ───
    repartitionSitesBrassage: builder.query<{
      session_id: number; description: string; mode: string;
      annee_academique: string; date_generation: string;
      genere_par: string | null;
      filtres: any;
      sites: Array<{
        site_nom: string;
        nb_salles_utilisees: number;
        salles: Array<{
          salle_nom: string; capacite: number; effectif: number;
          taux_remplissage: number | null; places_libres: number | null;
        }>;
        effectif_site: number;
        capacite_totale: number;
        taux_occupation: number | null;
        places_libres: number;
        classes: Array<{ label: string; nb: number }>;
        niveaux: Array<{ label: string; nb: number }>;
        poles: Array<{ label: string; nb: number }>;
      }>;
      nb_sites_utilises: number;
      sites_non_utilises: string[];
      total_effectif: number;
      total_capacite: number;
      total_places_libres: number;
      total_taux_occupation: number | null;
      total_salles_utilisees: number;
      placements_exclus: number;
    }, number>({
      query: (id) => `/brassage/${id}/repartition-sites/`,
      providesTags: ['Brassage'],
    }),

    candidatsRattrapage: builder.query<
      CandidatsRattrapage,
      { niveaux?: string[]; poles?: string[]; classes?: string[]; semestre?: number; annee_academique?: string }
    >({
      query: (params) => {
        const usp = new URLSearchParams();
        (params.niveaux || []).forEach(n => usp.append('niveaux', n));
        (params.poles || []).forEach(p => usp.append('poles', p));
        (params.classes || []).forEach(c => usp.append('classes', c));
        if (params.semestre) usp.set('semestre', String(params.semestre));
        if (params.annee_academique) usp.set('annee_academique', params.annee_academique);
        return `/brassage/candidats-rattrapage/?${usp.toString()}`;
      },
    }),

    autoDetecterTp: builder.mutation<
      { success: boolean; nb_marquees: number; total_tp: number; message: string },
      { reset_first?: boolean }
    >({
      query: (body) => ({
        url: '/matieres/auto-detecter-tp/',
        method: 'POST',
        body: body || {},
      }),
    }),

    toggleVisibiliteBrassage: builder.mutation<
      { success: boolean; id: number; visible_aux_etudiants: boolean; date_visibilite: string | null; visibilite_par_nom: string },
      { brassageId: number; visible?: boolean }
    >({
      query: ({ brassageId, visible }) => ({
        url: `/brassage/${brassageId}/toggle-visibilite/`,
        method: 'POST',
        body: visible !== undefined ? { visible } : {},
      }),
      invalidatesTags: ['Brassage'],
    }),

    // ─── HISTORIQUE ───
    getHistoriqueBrassage: builder.query<HistoriqueBrassageResponse, {
      annee_academique?: string;
      date_debut?: string;
      date_fin?: string;
    }>({
      query: (filters = {}) => {
        const qs = new URLSearchParams();
        if (filters.annee_academique) qs.set('annee_academique', filters.annee_academique);
        if (filters.date_debut) qs.set('date_debut', filters.date_debut);
        if (filters.date_fin) qs.set('date_fin', filters.date_fin);
        const s = qs.toString();
        return `/brassage/historique/${s ? '?' + s : ''}`;
      },
      providesTags: ['Brassage'],
    }),
    rechercherEtudiant: builder.query<
      RechercheEtudiantResponse,
      { q: string; session_id?: number }
    >({
      query: ({ q, session_id }) => {
        const qs = new URLSearchParams({ q });
        if (session_id) qs.set('session_id', String(session_id));
        return `/brassage/rechercher-etudiant/?${qs}`;
      },
    }),

    // ─── AJOUT ÉTUDIANT TARDIF ───
    getEtudiantsNonPlaces: builder.query<
      { resultats: EtudiantNonPlace[]; total: number },
      { sessionId: number; q?: string; all?: boolean }
    >({
      query: ({ sessionId, q, all }) => {
        const params = new URLSearchParams();
        if (all) params.set('all', 'true');
        else if (q) params.set('q', q);
        return `/brassage/${sessionId}/etudiants-non-places/?${params.toString()}`;
      },
      providesTags: ['Brassage'],
    }),
    getSallesDisponibles: builder.query<
      SalleDisponible[],
      { sessionId: number }
    >({
      query: ({ sessionId }) => `/brassage/${sessionId}/salles-disponibles/`,
      providesTags: ['Brassage'],
    }),
    ajouterEtudiantBrassage: builder.mutation<
      { success: boolean; message: string; mode_placement?: 'auto' | 'manuel'; placement: any },
      // `salle_id` optionnel : si absent ou null, backend fait un auto-placement
      // équilibré (mode-aware selon session.mode).
      { sessionId: number; matricule: string; salle_id?: number | null; auto?: boolean }
    >({
      query: ({ sessionId, ...body }) => ({
        url: `/brassage/${sessionId}/ajouter-etudiant/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Brassage'],
    }),

    // ─── DÉPLACER ÉTUDIANTS ───
    deplacerEtudiantsBrassage: builder.mutation<
      {
        success: boolean;
        nb_deplaces: number;
        nb_ignores: number;
        message: string;
        deplacements: {
          placement_id: number; etudiant: string; matricule: string;
          classe: string; de: string; vers: string;
        }[];
        salle_destination: {
          id: number; nom: string; site_nom: string;
          capacite: number; occupees: number; places_restantes: number;
        };
      },
      {
        sessionId: number;
        placement_ids: number[];
        salle_id_destination: number;
        motif?: string;
      }
    >({
      query: ({ sessionId, ...body }) => ({
        url: `/brassage/${sessionId}/deplacer-etudiants/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Brassage'],
    }),
  }),
});

export interface EtudiantNonPlace {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  niveau: string;
  pole: string;
}

export interface SalleDisponible {
  id: number;
  nom: string;
  site_nom: string;
  capacite: number;
  occupees: number;
  places_restantes: number;
}

export interface RechercheEtudiantResultat {
  id: number;              // = placement_id (utile pour déplacer)
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  pole: string;
  niveau: string;
  site: string;
  salle: string;
  salle_id: number;        // id salle actuelle
}

export interface RechercheEtudiantResponse {
  resultats: RechercheEtudiantResultat[];
  total: number;
  session: { id: number; description: string; date_generation: string; mode: string } | null;
}

export const {
  useGetSitesQuery,
  useCreateSiteMutation,
  useUpdateSiteMutation,
  useDeleteSiteMutation,
  useGetSallesQuery,
  useCreateSalleMutation,
  useUpdateSalleMutation,
  useDeleteSalleMutation,
  useGenererBrassageMutation,
  useGenererBrassagePourSessionMutation,
  useVerifierBrassageMutation,
  useLazyGetStatsSessionQuery,
  useLazyGetResultatsParSalleQuery,
  useLazyGetResultatsParSiteQuery,
  useLazyGetResultatsParClasseQuery,
  useGetHistoriqueBrassageQuery,
  useDeleteSessionBrassageMutation,
  useToggleVisibiliteBrassageMutation,
  useModifierDescriptionBrassageMutation,
  useRepartitionSitesBrassageQuery,
  useLazyRepartitionSitesBrassageQuery,
  useCandidatsRattrapageQuery,
  useLazyCandidatsRattrapageQuery,
  useAutoDetecterTpMutation,
  useLazyRechercherEtudiantQuery,
  useLazyGetEtudiantsNonPlacesQuery,
  useGetEtudiantsNonPlacesQuery,
  useGetSallesDisponiblesQuery,
  useLazyGetSallesDisponiblesQuery,
  useAjouterEtudiantBrassageMutation,
  useDeplacerEtudiantsBrassageMutation,
} = brassageApi;
